/**
 * Focus helpers for keyboard-first forms (EXT-6524).
 *
 * "Tab-skipped" means a control that stays clickable but carries
 * `tabindex="-1"`, so native Tab passes over it. Arrow-style navigation
 * (Alt+arrow in Order Entry) still wants to land on those, so every lookup
 * here takes `includeTabSkipped`.
 */

export interface FocusableFieldOptions {
  /** Include controls with `tabindex="-1"`. Default false. */
  includeTabSkipped?: boolean;
}

export type FocusDirection = 'up' | 'down' | 'left' | 'right';

const FIELD_SELECTOR = [
  'input:not([type="hidden"])',
  'textarea',
  'select',
  'button',
  'mat-select',
  '[tabindex]',
].join(', ');

const ARROW_DIRECTIONS: Record<string, FocusDirection> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
};

/** The direction of an Alt+arrow press (no other modifier), or null. */
export function altArrowDirection(event: KeyboardEvent): FocusDirection | null {
  if (!event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
    return null;
  }
  return ARROW_DIRECTIONS[event.key] ?? null;
}

function isDisabled(el: HTMLElement): boolean {
  if ((el as HTMLInputElement).disabled) return true;
  return el.getAttribute('aria-disabled') === 'true';
}

/**
 * Hidden by layout: `display: none`, `visibility: hidden` (a collapsed
 * mat-expansion-panel body), or inside an `inert`/`hidden` ancestor.
 * `display: contents` hosts are handled because the check is on the field
 * itself, never on its wrapper.
 */
function isHidden(el: HTMLElement): boolean {
  if (el.closest('[hidden], [inert]')) return true;
  if (getComputedStyle(el).visibility === 'hidden') return true;
  for (let node: HTMLElement | null = el; node; node = node.parentElement) {
    if (getComputedStyle(node).display === 'none') return true;
  }
  return false;
}

/**
 * Whether `el` is a field focus can move to.
 */
export function isFocusableField(
  el: Element,
  options: FocusableFieldOptions = {},
): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  if (!el.matches(FIELD_SELECTOR)) return false;
  if (isDisabled(el)) return false;
  const tabindex = el.getAttribute('tabindex');
  if (tabindex === '-1' && !options.includeTabSkipped) return false;
  return !isHidden(el);
}

/**
 * Focusable fields under `root`, in DOM order.
 */
export function getFocusableElements(
  root: ParentNode = document,
  options: FocusableFieldOptions = {},
): HTMLElement[] {
  return Array.from(root.querySelectorAll(FIELD_SELECTOR)).filter(
    (el): el is HTMLElement => isFocusableField(el, options),
  );
}

/**
 * Focus the next (or, with `step = -1`, previous) Tab stop after `current`
 * in document order. Returns the element focused, or null at either end.
 */
export function focusNextFocusableElement(
  current: HTMLElement,
  step: 1 | -1 = 1,
): HTMLElement | null {
  const elements = getFocusableElements(document);
  const i = elements.indexOf(current);
  const next = i > -1 ? elements[i + step] : undefined;
  if (!next) return null;
  next.focus();
  return next;
}

function center(rect: DOMRect): { x: number; y: number } {
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
}

/**
 * The candidate nearest to `from` in `direction` on screen, or null.
 *
 * Distance along the direction counts once and sideways drift counts
 * double, so Alt+Down from a field picks the one below it rather than a
 * closer field diagonally off to the side.
 */
export function findNearestInDirection(
  from: HTMLElement,
  candidates: readonly HTMLElement[],
  direction: FocusDirection,
): HTMLElement | null {
  const origin = from.getBoundingClientRect();
  const o = center(origin);
  let best: HTMLElement | null = null;
  let bestScore = Infinity;

  for (const el of candidates) {
    if (el === from || el.contains(from) || from.contains(el)) continue;
    const rect = el.getBoundingClientRect();
    const c = center(rect);
    let along: number;
    let across: number;
    switch (direction) {
      case 'down':
        along = rect.top - origin.bottom;
        across = Math.abs(c.x - o.x);
        if (c.y <= o.y) continue;
        break;
      case 'up':
        along = origin.top - rect.bottom;
        across = Math.abs(c.x - o.x);
        if (c.y >= o.y) continue;
        break;
      case 'right':
        along = rect.left - origin.right;
        across = Math.abs(c.y - o.y);
        if (c.x <= o.x) continue;
        break;
      case 'left':
        along = origin.left - rect.right;
        across = Math.abs(c.y - o.y);
        if (c.x >= o.x) continue;
        break;
    }
    const score = Math.max(along, 0) + across * 2;
    if (score < bestScore) {
      bestScore = score;
      best = el;
    }
  }
  return best;
}

/**
 * The candidate whose horizontal centre is closest to `from`'s, used for
 * "same column on the next line" in a grid whose rows share column widths.
 */
export function findNearestByColumn(
  from: HTMLElement,
  candidates: readonly HTMLElement[],
): HTMLElement | null {
  const x = center(from.getBoundingClientRect()).x;
  let best: HTMLElement | null = null;
  let bestDistance = Infinity;
  for (const el of candidates) {
    const distance = Math.abs(center(el.getBoundingClientRect()).x - x);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = el;
    }
  }
  return best;
}
